import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icons from '../../../../components/Common/Icons/Icons';
import { Button } from '../../../../components/Common/UI/Button';
import { cn } from '../../../../utils/styles';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../components/Common/UI/Accordion';
import { accountData, IAccountData } from './Data';
import MenuIcon from '../Menu/MenuIcon';
import Link from '../../../../components/Common/UI/Link';
import {
  getAccountFromStorage,
  IAccountProps,
  setAccountInStorage,
  useAccountContextProvider,
} from '../../../context/account';

const viewerUpdatesRaw = localStorage.getItem('viewerUpdates');

const ALLOWED_ACCOUNTS_FOR_NON_ADMIN = viewerUpdatesRaw
  ? (JSON.parse(viewerUpdatesRaw).find((u: IAccountData) => u.id === '1')?.selectedAccounts ?? [])
  : ['****4560', '****4564'];

function Account() {
  const account = getAccountFromStorage();
  const { handeUpdateAccount } = useAccountContextProvider();

  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [isAuthorizedUser, setIsAuthorizedUser] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeGroups, setActiveGroups] = useState<string[]>([]);

  const hiddenDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tempViewers = JSON.parse(localStorage.getItem('tempViewers') || '[]');

    setIsAuthorizedUser(tempViewers.length > 0);
  }, []);

  const filteredAccountData = useMemo(() => {
    let data = accountData;

    if (isAuthorizedUser) {
      data = accountData
        .filter(el => el.type === 'checking' || el.type === 'loan')
        .map(group => ({
          ...group,
          accounts: group.accounts.filter(acc => ALLOWED_ACCOUNTS_FOR_NON_ADMIN.includes(acc.number)),
        }))
        .filter(group => group.accounts.length > 0);
    }

    const query = searchQuery.trim().toLowerCase();

    if (!query) return data;

    return data
      .map(group => {
        const accounts = group.accounts.filter(acc => {
          return acc.title.toLowerCase().includes(query) || acc.number.toLowerCase().includes(query);
        });

        return {
          ...group,
          accounts,
        };
      })
      .filter(group => group.accounts.length > 0);
  }, [isAuthorizedUser, searchQuery]);

  /** OPEN ALL GROUPS WHEN SEARCH */
  useEffect(() => {
    if (searchQuery.trim()) {
      setActiveGroups(filteredAccountData.map(el => `item-${el.id}`));
    } else {
      setActiveGroups([]);
    }
  }, [searchQuery, filteredAccountData]);

  /** CLOSE DROPDOWN */
  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
    setActiveGroups([]);
  };

  /** CLICK OUTSIDE */
  useEffect(() => {
    const handleBodyClick = (e: MouseEvent) => {
      if (hiddenDivRef.current && !hiddenDivRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };

    document.body.addEventListener('click', handleBodyClick);

    return () => document.body.removeEventListener('click', handleBodyClick);
  }, []);

  /** OPEN MENU */
  const handleShow = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  /** SET ACCOUNT */
  const handleSetAccount = (payload: IAccountProps) => {
    handeUpdateAccount(payload);
    setAccountInStorage(payload);
  };

  /** RESET ACCOUNT ON DASHBOARD */
  useEffect(() => {
    if (location.pathname === '/guidance' || location.pathname === '/guidance/') {
      handleSetAccount({ name: '', number: '' });
    }
  }, [location.pathname]);

  return (
    <>
      <div className={cn('bg-black fixed hidden opacity-50 z-40 w-screen h-screen top-0 left-0', { block: isOpen })} />

      <div className="relative">
        <Button
          onClick={handleShow}
          className="w-full p-[5px_10px] h-[65px] !bg-primary rounded-[7px] text-white font-medium
          grid grid-cols-[25px,1fr,20px] gap-[7px] items-center transition-none"
        >
          <div className="flex items-center justify-center bg-white rounded-[50%] w-[25px] h-[25px]">
            <Icons.UserCheck className="w-[17px] h-[17px] [&_path]:fill-primary" />
          </div>

          <div className={cn('text-left text-[13px]', { 'text-[16px]': account?.number?.length })}>
            {account?.number?.length ? (
              <div>
                <div className="leading-[17px]">{account?.number}</div>
                <span className="leading-[15px] text-[13px]">{account?.name}</span>
              </div>
            ) : (
              'Select An Account'
            )}
          </div>

          <Icons.AccountSelectArrow />
        </Button>

        {isOpen && (
          <div ref={hiddenDivRef} className="absolute z-50">
            <div
              className="w-[365px] rounded-lg bg-white relative
              before:w-[30px] before:h-[30px] before:bg-white before:absolute
              before:rotate-45 before:top-[-15px] before:z-[-1] before:left-[130px]"
            >
              {/* HEADER */}
              <div className="px-[10px] py-[18px] flex justify-between items-center">
                <div className="text-base">Select An Account</div>

                <div className="flex gap-[5px] items-center">
                  <Link
                    to="/guidance"
                    className="uppercase underline text-addition6 opacity-80 hover:opacity-60"
                    onClick={handleClose}
                  >
                    Dashboard
                  </Link>

                  <Icons.PlusFilled
                    className="text-black rotate-45 w-[20px] h-[20px] cursor-pointer"
                    onClick={handleClose}
                  />
                </div>
              </div>

              {/* SEARCH */}
              <div className="px-[10px] pb-[10px] border-b">
                <div className="relative">
                  <Icons.Search className="absolute left-[10px] top-1/2 -translate-y-1/2 w-[16px] h-[16px] text-gray-400" />

                  <input
                    placeholder="Search by type, name or number..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-[32px] pr-[30px] py-[8px] border rounded-md text-[14px]
                    focus:outline-none focus:border-primary"
                  />

                  {searchQuery && (
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={e => {
                        e.stopPropagation();
                        setSearchQuery('');
                      }}
                      className="absolute right-[8px] top-1/2 -translate-y-1/2"
                    >
                      <Icons.PlusFilled className="w-[14px] h-[14px] rotate-45" />
                    </button>
                  )}
                </div>
              </div>

              {/* SCROLL AREA */}
              <div className="max-h-[420px] overflow-y-auto px-[10px] py-[5px]">
                <Accordion type="multiple" value={activeGroups} onValueChange={setActiveGroups}>
                  {filteredAccountData.length === 0 ? (
                    <div className="py-[20px] text-center text-gray-500 text-[14px]">No accounts found</div>
                  ) : (
                    filteredAccountData.map(el => (
                      <AccordionItem key={el.id} value={`item-${el.id}`} className="border-b-0">
                        <AccordionTrigger className="p-0 h-[54px] justify-start gap-[5px]">
                          <MenuIcon
                            icon={<Icons.PlusMinusAnimated active={!activeGroups.includes(`item-${el.id}`)} />}
                            className="w-[35px] h-[35px] [&_svg_path]:fill-secondary-70"
                          />

                          <MenuIcon
                            icon={el.icon}
                            className={cn('[&_svg_path]:fill-white w-[35px] h-[35px]')}
                            style={{ background: el.color, borderColor: el.color }}
                          />

                          <div className="text-secondary-80 text-[18px] font-medium">{el.label}</div>
                        </AccordionTrigger>

                        {el.accounts.map(acc => (
                          <AccordionContent
                            key={acc.id}
                            onClick={e => {
                              e.stopPropagation();

                              handleSetAccount({
                                name: acc.title,
                                number: acc.number,
                              });

                              handleClose();

                              if (el.type === 'mortgage') {
                                navigate('/guidance/statements/mortgage-statements');
                              } else if (el.type === 'visa') {
                                navigate('/guidance/statements/visa-classic');
                              } else {
                                navigate('/guidance/statements/checking-statements');
                              }
                            }}
                            className="p-0 cursor-pointer h-[39px] px-[10px] font-medium text-[18px]
                            flex justify-between items-center border rounded-lg mb-[5px]
                            border-l-[5px] bg-addition6-3"
                            style={{ borderLeftColor: el.color }}
                          >
                            <div className="opacity-80">{acc.title}</div>
                            <div className="text-[14px] text-gray-600">{acc.number}</div>
                          </AccordionContent>
                        ))}
                      </AccordionItem>
                    ))
                  )}
                </Accordion>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Account;
